import type { DashboardController } from './types'

/**
 * The "danger" red used for offline site names/borders, the Major alarm
 * badge, and the per-device header's Grid badge - a single explicit value
 * rather than the design system's --error token (an orange-leaning red),
 * per an explicit request to match the Grid column's (more standard) red
 * everywhere this "offline/danger" meaning is shown, without touching
 * --error itself (still used elsewhere for form-validation-style errors).
 */
export const OFFLINE_RED = '#dc2626'

/**
 * Column value fallback chains, ported verbatim from
 * includes/portifolio_list/table_row.html's per-cell Jinja logic. Each
 * returns the raw numeric value (or undefined if none of the fallback
 * register keys are present) - formatting/dash-display is the caller's job.
 */
export function siteSolarKw(registers: DashboardController['registers']): number | undefined {
  const solar = toNum(registers.solar_tot_w)
  const pvdcdc = toNum(registers.pvdcdc_dc_tot_out_w)
  if (solar !== undefined && pvdcdc !== undefined) return solar + pvdcdc
  return solar ?? pvdcdc
}

export function gridKw(registers: DashboardController['registers']): number | undefined {
  return toNum(registers.grid_ac_w) ?? toNum(registers.em_grid_1_em_w_ac) ?? toNum(registers.grid_tot_w)
}

export function batteryKw(registers: DashboardController['registers']): number | undefined {
  return (
    toNum(registers.batt_ac_tot_w) ??
    toNum(registers.hi_solar_dc_tot_w) ??
    toNum(registers.batt_dc_tot_w) ??
    toNum(registers.sacu_batt_dc_tot_w)
  )
}

/** The controller's two calculated genset-power registers. */
const GENSET_CALCULATED_REGISTERS = ['em_genset_ac_w', 'genset_ac_w'] as const

/** Genset power. Between the two CALCULATED registers, the first carrying a
 * NON-ZERO reading wins, else the first reported (0 = gensets off). Only a
 * site reporting neither falls back to genset_tot_w - the pre-rename Sitka
 * key, same last-resort position as legacy's header badge.
 * - 2026-10-22: stopped at the first register merely PRESENT - every site
 *   publishes em_genset_ac_w, mostly as a structural 0, so Oltepesi Plants
 *   (real power in genset_ac_w = gsc_ac_tot_w, ~95 kW) showed Genset 0.
 * - 2026-10-26: the non-zero preference must NOT reach genset_tot_w: Fleur
 *   reports em_genset_ac_w = genset_ac_w = 0 (24h: 0 at all 288 points,
 *   load = solar + grid to 0.01 kW) while its snapshot carries a stray
 *   genset_tot_w = em_genset_1_em_w_ac = 461 kW - which showed a genset
 *   "Running" that was off. */
export function gensetKw(registers: DashboardController['registers']): number | undefined {
  let firstReported: number | undefined
  for (const key of GENSET_CALCULATED_REGISTERS) {
    const value = toNum(registers[key])
    if (value === undefined) continue
    if (value !== 0) return value
    firstReported ??= value
  }
  return firstReported ?? toNum(registers.genset_tot_w)
}

/** Not used by the fleet dashboard table, but part of the per-device Details
 * page header widget (includes/device_detail_navbar.html's "Load" badge). */
export function loadKw(registers: DashboardController['registers']): number | undefined {
  return toNum(registers.load_w)
}

export function solarYieldKwhr(registers: DashboardController['registers']): number | undefined {
  return toNum(registers.solar_tot_energy_yield_day)
}

export function prPercent(registers: DashboardController['registers']): number | undefined {
  return toNum(registers.solar_perf_ratio_day)
}

export function irradianceWm2(registers: DashboardController['registers']): number | undefined {
  return toNum(registers.exs_1_exs_irradiation)
}

function toNum(v: unknown): number | undefined {
  if (v === null || v === undefined || v === '') return undefined
  const n = Number(v)
  return Number.isNaN(n) ? undefined : n
}

/** Same status classification the legacy template's three-pass loop used. */
export type StatusGroup = 'offline' | 'online' | 'unknown'

export function statusGroup(online: DashboardController['online']): StatusGroup {
  if (online === false) return 'offline'
  if (online === true) return 'online'
  return 'unknown'
}

/**
 * Hardcoded exclusion from the legacy templates (no explanation given
 * there either) - applied to the offline group for every role, and
 * additionally to the unknown-status group for clients only (admin/staff
 * still see it there).
 */
const HIDDEN_CONTROLLER_ID = '0C9D920C9CEE'

export function isHiddenForRole(controllerId: string, group: StatusGroup, role: 'admin' | 'staff' | 'client'): boolean {
  if (controllerId !== HIDDEN_CONTROLLER_ID) return false
  if (group === 'offline') return true
  if (group === 'unknown' && role === 'client') return true
  return false
}

/** Ports includes/admin_dashboard.html's/staff_dashboard.html's header capacity sum (kW -> MW). */
export function totalCapacityMw(controllers: DashboardController[]): number {
  return (
    controllers.reduce((sum, c) => {
      const n = toNum(c.capacity)
      return sum + (n ?? 0)
    }, 0) / 1000
  )
}

export function formatCapacity(capacity: DashboardController['capacity']): string {
  const n = toNum(capacity)
  if (n !== undefined && n > 0) return n.toFixed(2)
  return capacity != null ? String(capacity) : ''
}

/** Ports table_row.html's "Sep 10,1234hr" -> ["Sep 10", "12:34"] split. */
export function formatLastSeen(lastseen: string | null): { date: string; time: string } | null {
  if (!lastseen) return null
  const withoutHr = lastseen.replace(/hr$/, '')
  const [datePart, timePart] = withoutHr.split(',')
  if (!timePart) return { date: datePart ?? '', time: '' }
  const digits = timePart.padStart(4, '0')
  return { date: datePart, time: `${digits.slice(0, 2)}:${digits.slice(2, 4)}` }
}
